import { useMutation, useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { Badge, ORDER_TONE, PAYMENT_TONE, POLICY_TONE, type Tone } from '../components/badge'
import { Button, Card, ErrorNote } from '../components/ui'
import { ApiError } from '../lib/api'
import { customerName } from '../lib/customers-api'
import {
  formatJalaliDate,
  formatJalaliDateTime,
  formatMobile,
  formatToman,
  toPersianDigits,
} from '../lib/fa'
import {
  getOrder,
  getPolicyDocument,
  type OrderDetailDto,
  type TimelineEventDto,
} from '../lib/sales-api'

const messageOf = (error: unknown): string =>
  error instanceof ApiError
    ? error.code === 'NOT_FOUND'
      ? 'این سفارش پیدا نشد.'
      : error.messageFa
    : 'مشکلی پیش آمد. دوباره تلاش کنید.'

/** One order: what was sold, how it was paid, what came of it, and when. Read-only. */
export function OrderDetailPage() {
  const { id = '' } = useParams()
  const location = useLocation()
  const order = useQuery({ queryKey: ['order', id], queryFn: () => getOrder(id) })

  // Back to the same list, search and page, if that is where we came from.
  const backTo = (location.state as { from?: string } | null)?.from ?? '/orders'
  const backLabel = backTo.startsWith('/customers')
    ? 'مشتری'
    : backTo.startsWith('/payments')
      ? 'پرداخت‌ها'
      : 'سفارش‌ها'

  return (
    <div className="max-w-7xl">
      <Link to={backTo} className="mb-4 inline-block text-sm font-semibold text-brand-600">
        → {backLabel}
      </Link>

      {order.isPending ? (
        <p className="text-sm text-muted">در حال بارگذاری…</p>
      ) : order.isError ? (
        <ErrorNote>{messageOf(order.error)}</ErrorNote>
      ) : (
        <OrderDetail data={order.data} />
      )}
    </div>
  )
}

function OrderDetail({ data }: { data: OrderDetailDto }) {
  const location = useLocation()
  const from = `${location.pathname}${location.search}`
  const { quote, offer, policy } = data
  const name = customerName(data.customer)

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">
          {quote.productTitleFa} — {offer.insurerName}
        </h1>
        <Badge tone={ORDER_TONE[data.status]}>{data.statusFa}</Badge>
        {data.paidWithoutPolicy ? <Badge tone="warn">پرداخت‌شده، بدون بیمه‌نامه</Badge> : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="min-w-0">
          <Card>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm lg:grid-cols-3">
              <Field label="مشتری">
                <Link
                  to={`/customers/${data.customer.id}`}
                  state={{ from }}
                  className="hover:text-brand-600"
                >
                  {name ?? 'بدون نام'}
                </Link>
                <div
                  className="text-xs font-normal text-muted"
                  dir="ltr"
                  style={{ textAlign: 'right' }}
                >
                  {formatMobile(data.customer.mobile)}
                </div>
              </Field>
              <Field label="مبلغ">{formatToman(data.amount)}</Field>
              <Field label="ثبت سفارش">{formatJalaliDateTime(data.createdAt)}</Field>
              {quote.plateFa ? <Field label="پلاک">{quote.plateFa}</Field> : null}
              <Field label="شماره سفارش">
                <span className="font-mono text-xs" dir="ltr">
                  {data.id}
                </span>
              </Field>
              <Field label="آخرین تغییر">{formatJalaliDateTime(data.updatedAt)}</Field>
            </dl>
          </Card>

          <PolicySection policy={policy} />

          <Section
            title="پرداخت‌ها"
            empty={data.payments.length === 0 ? 'تلاشی برای پرداخت نشده است.' : null}
          >
            <Table head={['#', 'درخواست / نتیجه', 'مبلغ', 'وضعیت', 'رهگیری و کارت', 'شناسه درگاه']}>
              {data.payments.map((payment, index) => (
                <tr
                  key={payment.id}
                  id={`payment-${payment.id}`}
                  className="border-b border-line last:border-0"
                >
                  <td className="px-4 py-3 text-muted">{toPersianDigits(index + 1)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">
                    {formatJalaliDateTime(payment.requestedAt)}
                    <div className="text-xs">
                      {payment.verifiedAt ? formatJalaliDateTime(payment.verifiedAt) : '—'}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{formatToman(payment.amount)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={PAYMENT_TONE[payment.status]}>{payment.statusFa}</Badge>
                  </td>
                  <Mono>
                    {payment.refId ?? '—'}
                    <div>{payment.cardMask ?? '—'}</div>
                  </Mono>
                  <Mono>
                    {payment.gateway}
                    <div className="max-w-32 truncate" title={payment.authority}>
                      {payment.authority}
                    </div>
                  </Mono>
                </tr>
              ))}
            </Table>
          </Section>

          <Section title="قیمت پیشنهادی (ثابت‌شده در استعلام)">
            <div className="grid gap-6 p-5 text-sm md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-xs text-muted">ریز مبلغ</h3>
                <dl className="space-y-1.5">
                  {offer.lineItems.map((item) => (
                    <div key={item.key} className="flex justify-between gap-4">
                      <dt>{item.labelFa}</dt>
                      <dd className="whitespace-nowrap">
                        {item.kind === 'DISCOUNT' ? '−' : ''}
                        {formatToman(Math.abs(item.amount))}
                      </dd>
                    </div>
                  ))}
                  <div className="flex justify-between gap-4 border-t border-line pt-1.5 font-semibold">
                    <dt>جمع</dt>
                    <dd className="whitespace-nowrap">{formatToman(offer.totalAmount)}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h3 className="mb-2 text-xs text-muted">پوشش‌ها</h3>
                <LabelValues items={offer.coverages} />
              </div>
            </div>
            <p className="border-t border-line px-5 py-3 text-xs text-muted">
              استعلام {formatJalaliDateTime(quote.createdAt)} — نسخه جدول نرخ{' '}
              {toPersianDigits(offer.rateTableVersion)}
              {quote.isSampleRates ? ' — نرخ‌های نمونه (غیرواقعی)' : ''}
            </p>
          </Section>

          <Section
            title="بیمه‌شدگان"
            empty={data.insured.length === 0 ? 'در سفارش بیمه‌شده‌ای ثبت نشده است.' : null}
          >
            <Table head={['نام', 'کد ملی', 'تاریخ تولد', 'گذرنامه']}>
              {data.insured.map((person, index) => (
                <tr key={index} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 font-medium">
                    {[person.firstName, person.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-4 py-3">
                    {person.nationalCode ? toPersianDigits(person.nationalCode) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {person.birthDate ? formatJalaliDate(person.birthDate) : '—'}
                  </td>
                  <Mono>{person.passportNo ?? '—'}</Mono>
                </tr>
              ))}
            </Table>
          </Section>
        </div>

        <aside>
          <h2 className="mb-3 font-semibold">رویدادها</h2>
          <Card>
            <Timeline events={data.timeline} />
          </Card>
          <p className="mt-2 text-xs text-muted">
            از زمان‌های ثبت‌شده در سفارش، پرداخت‌ها و بیمه‌نامه ساخته شده؛ تاریخچه کامل تغییر وضعیت
            ثبت نمی‌شود.
          </p>
        </aside>
      </div>
    </>
  )
}

function PolicySection({ policy }: { policy: OrderDetailDto['policy'] }) {
  const open = useMutation({
    mutationFn: async (documentUrl: string) => {
      // The document needs the staff token, so it cannot be a plain link: fetch, then open a blob.
      const html = await getPolicyDocument(documentUrl)
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      if (!window.open(url, '_blank')) window.location.assign(url)
    },
  })

  if (!policy) {
    return (
      <Section title="بیمه‌نامه" empty="برای این سفارش بیمه‌نامه‌ای صادر نشده است.">
        {null}
      </Section>
    )
  }

  return (
    <Section title="بیمه‌نامه">
      <div className="p-5">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm lg:grid-cols-3">
          <Field label="شماره">
            <span dir="ltr">{toPersianDigits(policy.policyNumber)}</span>
          </Field>
          <Field label="وضعیت">
            <Badge tone={POLICY_TONE[policy.status]}>{policy.statusFa}</Badge>
          </Field>
          <Field label="صدور">{formatJalaliDateTime(policy.issuedAt)}</Field>
          <Field label="اعتبار">
            {formatJalaliDate(policy.startsAt)} تا {formatJalaliDate(policy.endsAt)}
          </Field>
          <Field label="محصول و بیمه‌گر">
            {policy.productTitleFa} — {policy.insurerName}
          </Field>
          <Field label="مبلغ و بیمه‌شدگان">
            {policy.totalAmount !== null ? formatToman(policy.totalAmount) : '—'}،{' '}
            {toPersianDigits(policy.insuredCount)} نفر
          </Field>
        </dl>
        {policy.risk.length > 0 ? (
          <div className="mt-4 border-t border-line pt-4 text-sm">
            <h3 className="mb-2 text-xs text-muted">موضوع بیمه</h3>
            <LabelValues items={policy.risk} />
          </div>
        ) : null}
        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="secondary"
            loading={open.isPending}
            onClick={() => open.mutate(policy.documentUrl)}
          >
            نمایش سند بیمه‌نامه
          </Button>
          {open.isError ? <ErrorNote>{messageOf(open.error)}</ErrorNote> : null}
        </div>
      </div>
    </Section>
  )
}

const DOT: Record<Tone, string> = {
  good: 'bg-emerald-500',
  bad: 'bg-red-500',
  warn: 'bg-amber-500',
  neutral: 'bg-brand-500',
}

/** Oldest at the top. Future events are hollow; approximate ones say so. */
function Timeline({ events }: { events: TimelineEventDto[] }) {
  return (
    <ol className="relative border-s border-line ps-5">
      {events.map((event, index) => (
        <li key={`${event.kind}-${event.at}-${index}`} className="relative pb-5 last:pb-0">
          <span
            className={`absolute -start-[1.6rem] top-1 size-3 rounded-full ring-4 ring-card ${
              event.future ? `border-2 border-current bg-card text-muted` : DOT[event.tone]
            }`}
          />
          <p className={`text-sm font-medium ${event.future ? 'text-muted' : ''}`}>
            {event.titleFa}
          </p>
          <p className="text-xs text-muted">
            {event.approximate ? 'حدوداً ' : ''}
            {formatJalaliDateTime(event.at)}
            {event.future ? ' (در پیش)' : ''}
          </p>
          {event.detailFa ? (
            <p className="mt-0.5 text-xs text-strong">
              {event.paymentId ? (
                <a href={`#payment-${event.paymentId}`} className="hover:text-brand-600">
                  {event.detailFa}
                </a>
              ) : (
                event.detailFa
              )}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  )
}

function LabelValues({ items }: { items: { labelFa: string; valueFa: string }[] }) {
  if (items.length === 0) return <p className="text-muted">—</p>
  return (
    <dl className="space-y-1.5">
      {items.map((item, index) => (
        <div key={index} className="flex justify-between gap-4">
          <dt className="text-muted">{item.labelFa}</dt>
          <dd className="text-left">{item.valueFa}</dd>
        </div>
      ))}
    </dl>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="mb-1 text-xs text-muted">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  )
}

function Mono({ children }: { children: ReactNode }) {
  return (
    <td
      className="whitespace-nowrap px-4 py-3 font-mono text-xs text-muted"
      dir="ltr"
      style={{ textAlign: 'right' }}
    >
      {children}
    </td>
  )
}

function Section({
  title,
  empty = null,
  children,
}: {
  title: string
  empty?: string | null
  children: ReactNode
}) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 font-semibold">{title}</h2>
      <Card className="overflow-x-auto p-0">
        {empty ? <p className="p-5 text-sm text-muted">{empty}</p> : children}
      </Card>
    </section>
  )
}

function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <table className="w-full text-sm">
      <thead className="border-b border-line text-right text-muted">
        <tr>
          {head.map((label) => (
            <th key={label} className="px-4 py-3 font-medium">
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}
